import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  Router,
  RouterStateSnapshot,
} from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';

import { AppState } from '../../app.state';
import { UserState } from '../state/user.state';

@Injectable({
  providedIn: 'root',
})
export class RedirectionGuard implements CanActivate {
  constructor(
    private router: Router,
    private appState: AppState,
    private userState: UserState
  ) { }

  /**
   * state.url contains the complete URL being navigated to.
   *
   * Examples:
   *
   * /reports
   * /reports?createReport=compCheck
   * /reports/view?id=123
   *
   * route.url may be empty when the guard is placed on a child
   * route whose path is ''.
   */
  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> {
    return this.appState.companyInfo$.pipe(
      take(1),

      map((companyInfo: any) => {
        const userRole = this.userState.getUserRoleValue;

        /**
         * Get the top-level route from the complete target URL.
         *
         * /reports?createReport=compCheck → reports
         * /workspace/map                → workspace
         */
        const currentPath = this.getTopLevelRouteFromUrl(state.url);

        if (companyInfo?.appConfig?.valueReport?.isEnabled) {
          return this.handleValueReportRoute(
            currentPath,
            companyInfo,
            userRole
          );
        }

        if (
          companyInfo?.appConfig?.opportunityScanner?.isEnabled &&
          companyInfo?.alias === 'opscan'
        ) {
          return this.handleOpportunityScannerRoute(
            route,
            currentPath,
            companyInfo,
            userRole
          );
        }

        /**
         * No special redirection configuration.
         */
        return true;
      }),

      catchError((error) => {
        console.error('RedirectionGuard error:', error);

        this.router.navigate(['/not-found']);

        return of(false);
      })
    );
  }

  /**
   * Handles Value Report application route authorization.
   */
  private handleValueReportRoute(
    currentPath: string,
    companyInfo: any,
    userRole: string
  ): boolean {
    const isLoggedIn =
      !!this.appState.authTokenValue?.idToken &&
      !!userRole;

    if (!isLoggedIn) {
      this.router.navigate(['/signup/user']);
      return false;
    }

    /**
     * Get routes available for the logged-in role.
     *
     * Examples:
     *
     * /reports        → reports
     * reports         → reports
     * /workspace/map  → workspace
     */
    const allowedRoutes = this.getAllowedRoutes(
      companyInfo,
      userRole
    );
    /**
     * The requested route is allowed.
     *
     * Returning true preserves query parameters such as:
     *
     * /reports?createReport=compCheck
     */
    if (
      currentPath &&
      allowedRoutes.includes(currentPath)
    ) {
      return true;
    }

    /**
     * Redirect to the first allowed application route.
     */
    if (allowedRoutes.length > 0) {
      this.router.navigate([
        `/${allowedRoutes[0]}`,
      ]);

      return false;
    }

    /**
     * Logged-in user has no permitted routes.
     */
    this.router.navigate(['/signup/user']);

    return false;
  }

  /**
   * Handles Opportunity Scanner route authorization.
   */
  private handleOpportunityScannerRoute(
    route: ActivatedRouteSnapshot,
    currentPath: string,
    companyInfo: any,
    userRole: string
  ): boolean {
    const isLoggedIn =
      !!this.appState.authTokenValue?.idToken &&
      !!userRole;


    if (currentPath === 'workspace') {
      return true;
    }

    if (!isLoggedIn) {
      this.router.navigate(['/login']);
      return false;
    }

    const allowedRoutes = this.getAllowedRoutes(
      companyInfo,
      userRole
    );

    /**
     * Requested route is available for the user.
     */
    if (
      currentPath &&
      allowedRoutes.includes(currentPath)
    ) {
      return true;
    }

    /**
     * Keep your existing map-route handling.
     */
    const hasMapRedirect =
      route?.routeConfig?.children?.some(
        (child) =>
          child.redirectTo === 'map' ||
          child.path === 'map'
      );

    if (hasMapRedirect) {
      return true;
    }

    if (allowedRoutes.length > 0) {
      this.router.navigate([
        `/${allowedRoutes[0]}`,
      ]);
    } else {
      this.router.navigate(['/login']);
    }

    return false;
  }

  /**
   * Reads the first meaningful route segment from a full URL.
   *
   * /reports?createReport=compCheck → reports
   * /reports/view?id=123           → reports
   * /workspace/map                 → workspace
   */
  private getTopLevelRouteFromUrl(
    url: string
  ): string {
    if (!url) {
      return '';
    }

    /**
     * Remove query parameters and hash fragment.
     */
    const cleanUrl = url
      .split('?')[0]
      .split('#')[0];

    const segments = cleanUrl
      .split('/')
      .filter(Boolean);

    return segments[0] || '';
  }

  /**
   * Returns the top-level routes configured for a role.
   */
  // private getAllowedRoutes(
  //   companyInfo: any,
  //   userRole: string
  // ): string[] {
  //   const menuConfig =
  //     companyInfo?.appMenuConfig || [];

  //   return menuConfig
  //     .filter((menu: any) =>
  //       Array.isArray(menu?.roles) &&
  //       menu.roles.includes(userRole)
  //     )
  //     .map((menu: any) =>
  //       this.getTopLevelRouteFromUrl(
  //         menu?.routePath || ''
  //       )
  //     )
  //     .filter(
  //       (
  //         routePath: string,
  //         index: number,
  //         routes: string[]
  //       ) =>
  //         !!routePath &&
  //         routes.indexOf(routePath) === index
  //     );
  // }


  // Added additional logic to filter child 
  private getAllowedRoutes(
    companyInfo: any,
    userRole: string
  ): string[] {

    const menuConfig =
      companyInfo?.appMenuConfig || [];

    return menuConfig
      .filter((menu: any) =>
        Array.isArray(menu?.roles) &&
        menu.roles.includes(userRole)
      )
      .flatMap((menu: any) => {

        const routes = [];

        // Existing logic - parent route
        if (menu?.routePath) {
          routes.push(
            this.getTopLevelRouteFromUrl(
              menu.routePath
            )
          );
        }

        // Additional logic - child routes
        if (Array.isArray(menu?.children)) {
          menu.children
            .filter((child: any) =>
              Array.isArray(child?.roles) &&
              child.roles.includes(userRole)
            )
            .forEach((child: any) => {
              if (child?.routePath) {
                routes.push(
                  this.getTopLevelRouteFromUrl(
                    child.routePath
                  )
                );
              }
            });
        }

        return routes;
      })
      .filter(
        (
          routePath: string,
          index: number,
          routes: string[]
        ) =>
          !!routePath &&
          routes.indexOf(routePath) === index
      );
  }
}